/**
 * The Teams area's pure rules: how a team's problems read, and when a change
 * needs a second "are you sure".
 *
 * Kept import-free (types only) so `node --test` can load it straight from
 * source. The shapes are structural twins of the generated `TeamProblem` /
 * `TeamProblemCode`, narrowed to what these read.
 *
 * The problems themselves are computed by the server on every read (2 Oct);
 * nothing here decides whether a team has one, only how it is shown.
 */

/** Every problem the server reports, most urgent first. */
export const TEAM_PROBLEM_CODES = [
  "MANAGER_LEFT",
  "NO_MANAGER",
  "MEMBER_LEFT",
  "NO_MEMBERS",
  "MANAGER_NOT_MEMBER",
  "NO_TEAM_EMAIL",
] as const;

export type TeamProblemCodeValue = (typeof TEAM_PROBLEM_CODES)[number];

export interface TeamProblemLike {
  code: TeamProblemCodeValue;
  employee_name?: string | null;
}

/** One badge per kind of problem, with how often it occurs on the team. */
export interface ProblemBadge {
  code: TeamProblemCodeValue;
  count: number;
}

/**
 * Collapse a team's problems into badges, in urgency order.
 *
 * Three leavers are one "Member has left" badge counted three, not three
 * badges — the list row has to stay one line; the team page names each.
 */
export function problemBadges(problems: readonly TeamProblemLike[]): ProblemBadge[] {
  const counts = new Map<TeamProblemCodeValue, number>();
  for (const problem of problems) {
    counts.set(problem.code, (counts.get(problem.code) ?? 0) + 1);
  }
  return TEAM_PROBLEM_CODES.filter((code) => counts.has(code)).map((code) => ({
    code,
    count: counts.get(code) ?? 0,
  }));
}

/** Red for a person problem (somebody has left, nobody is in charge), amber otherwise. */
export function problemTone(code: TeamProblemCodeValue): "danger" | "warning" {
  return code === "MANAGER_LEFT" || code === "NO_MANAGER" || code === "MEMBER_LEFT"
    ? "danger"
    : "warning";
}

/** The `common` key for a problem's short badge text. */
export function problemBadgeKey(
  code: TeamProblemCodeValue,
): `teams.problem.${TeamProblemCodeValue}.badge` {
  return `teams.problem.${code}.badge`;
}

/** The `common` key for a problem's one-line explanation (takes `{{name}}`). */
export function problemLineKey(
  code: TeamProblemCodeValue,
): `teams.problem.${TeamProblemCodeValue}.line` {
  return `teams.problem.${code}.line`;
}

/** Badge text with its count, e.g. "Member has left (2)". A single one has no number. */
export function badgeText(label: string, count: number): string {
  return count > 1 ? `${label} (${count})` : label;
}

/**
 * Whether choosing `newManagerId` should ask "you will lose edit rights".
 *
 * Only for the team's own manager, only when they are not an admin (an admin
 * keeps access to every team), and only when the manager actually changes.
 */
export function needsHandoverConfirm(input: {
  isAdmin: boolean;
  youManage: boolean;
  currentManagerId: string | null | undefined;
  newManagerId: string | null | undefined;
}): boolean {
  const { isAdmin, youManage, currentManagerId, newManagerId } = input;
  return (
    !isAdmin &&
    youManage &&
    newManagerId != null &&
    newManagerId !== "" &&
    newManagerId !== currentManagerId
  );
}

/**
 * Whether the reader may change this team (8 Oct): the server's `can_edit`
 * (an admin, or the team's own manager). All of IT sees every team; for the
 * rest it is view only, so no add/remove/edit controls. Anything but `true`
 * is "no": the page never offers a button the API would refuse.
 */
export function canEditTeam(team: { can_edit?: boolean | null } | undefined): boolean {
  return team?.can_edit === true;
}

/** The loose shape the server accepts: something@something.tld. */
const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A trimmed team email, `null` for "clear it", or `undefined` if it is not an address. */
export function normaliseTeamEmail(value: string): string | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  return EMAIL_SHAPE.test(trimmed) ? trimmed : undefined;
}
